/**
 * TrustedDevicesSettings Component
 * Shows and manages trusted devices for the user's account
 */
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Monitor,
  Smartphone,
  Tablet,
  Trash2,
  Loader2,
  Shield,
  Clock,
  LogOut,
  AlertTriangle
} from 'lucide-react';

const TrustedDevicesSettings = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [signingOut, setSigningOut] = useState(false);
  const { logout } = useAuth();

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await api.get('/passkey/devices');
      setDevices(response.data.devices || []);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDevice = async (fingerprint) => {
    if (!confirm('Remove this device from trusted list? You\'ll get an alert next time it logs in.')) {
      return;
    }
    setRemovingId(fingerprint);
    try {
      await api.delete('/passkey/device/' + fingerprint);
      toast.success('Device removed');
      setDevices(prev => prev.filter(d => d.fingerprint !== fingerprint));
    } catch (error) {
      toast.error('Failed to remove device');
    } finally {
      setRemovingId(null);
    }
  };

  const handleSignOutEverywhere = async () => {
    if (!confirm('This will sign you out from ALL devices including this one. You will need to log in again. Continue?')) {
      return;
    }
    setSigningOut(true);
    try {
      await api.post('/passkey/sign-out-everywhere');
      toast.success('Signed out from all devices');
      // Log out current session
      setTimeout(() => {
        logout();
      }, 1500);
    } catch (error) {
      toast.error('Failed to sign out');
      setSigningOut(false);
    }
  };

  const getDeviceIcon = (deviceInfo) => {
    if (deviceInfo?.is_mobile) return Smartphone;
    if (deviceInfo?.is_tablet) return Tablet;
    return Monitor;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-slate-900 rounded-lg p-6 border border-slate-800">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 rounded-lg p-6 border border-slate-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/20 rounded-lg">
            <Shield className="h-6 w-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Trusted Devices</h3>
            <p className="text-sm text-slate-400">Devices that won't trigger login alerts</p>
          </div>
        </div>
      </div>

      {/* Sign Out Everywhere Button */}
      <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-red-400 font-medium text-sm">Sign Out Everywhere</p>
            <p className="text-red-300/70 text-xs mt-1">Revoke all sessions and remove all trusted devices</p>
          </div>
          <Button
            onClick={handleSignOutEverywhere}
            disabled={signingOut}
            size="sm"
            className="bg-red-600 hover:bg-red-500 text-white"
            data-testid="sign-out-everywhere-btn"
          >
            {signingOut ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <LogOut className="h-4 w-4 mr-2" />
            )}
            {signingOut ? 'Signing out...' : 'Sign Out All'}
          </Button>
        </div>
      </div>

      {/* Device List */}
      <div className="space-y-3">
        {devices.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-slate-700 rounded-lg">
            <Monitor className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No trusted devices yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Devices will be added when you choose to trust them after login
            </p>
          </div>
        ) : (
          devices.map((device) => {
            const DeviceIcon = getDeviceIcon(device.device_info);
            return (
              <div
                key={device.fingerprint}
                className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-lg"
                data-testid={`device-item-${device.fingerprint}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-slate-700 rounded-lg">
                    <DeviceIcon className="h-5 w-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{device.device_name}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
                      <span>{device.device_info?.browser}</span>
                      <span>{device.device_info?.os}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Last: {formatDate(device.last_seen)}
                      </span>
                      <span className="text-emerald-400">{device.login_count} logins</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveDevice(device.fingerprint)}
                  disabled={removingId === device.fingerprint}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                  data-testid={`remove-device-${device.fingerprint}`}
                >
                  {removingId === device.fingerprint ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            );
          })
        )}
      </div>

      {/* Info box */}
      <div className="mt-6 p-4 bg-slate-800/30 border border-slate-700 rounded-lg">
        <h4 className="text-sm font-medium text-slate-300 mb-2">How trusted devices work</h4>
        <ul className="text-xs text-slate-500 space-y-1">
          <li>• When you log in from a new device, you'll receive an email alert</li>
          <li>• You can mark devices as trusted to skip future alerts</li>
          <li>• Trusted devices use browser fingerprinting (not location)</li>
          <li>• Remove a device if you lose it or want to be alerted again</li>
        </ul>
      </div>
    </div>
  );
};

export default TrustedDevicesSettings;
